import * as THREE from 'three';
import { type PlayerModelId, DEFAULT_PLAYER_MODEL, setSelectedPlayerModel } from './playerModel.js';
import { getPlayerName, setPlayerName } from './playerName.js';
import { MAX_DISPLAY_NAME_LENGTH } from '@cozy/shared';

export class MainMenu {
  // gameInstance is the Game (game.js, not yet migrated); typed loosely.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gameInstance: any;
  menuElement!: HTMLDivElement;
  backgroundScene: THREE.Scene | null;
  backgroundCamera: THREE.PerspectiveCamera | null;
  backgroundRenderer: THREE.WebGLRenderer | null;
  animationId: number | null;
  isVisible: boolean;
  floatingElements: THREE.Object3D[] = [];
  currentModalType?: 'load' | 'save';
  /** Avatar chosen for a NEW game. Loaded saves restore their own model. */
  selectedModel: PlayerModelId = DEFAULT_PLAYER_MODEL;

  constructor(gameInstance: any) {
    this.gameInstance = gameInstance;
    this.backgroundScene = null;
    this.backgroundCamera = null;
    this.backgroundRenderer = null;
    this.animationId = null;
    this.isVisible = true;
    
    this.createMainMenu();
    this.setupBackground();
    this.startBackgroundAnimation();
  }

  /** Query a required element inside the menu (asserts presence; the markup is static). */
  qs(selector: string): HTMLElement {
    return this.menuElement.querySelector<HTMLElement>(selector)!;
  }

  createMainMenu() {
    // Create main menu container
    this.menuElement = document.createElement('div');
    this.menuElement.className = 'main-menu';
    this.menuElement.id = 'main-menu';
    
    this.menuElement.innerHTML = `
      <div class="menu-background"></div>
      <div class="menu-content">
        <div class="game-title">
          <h1>Cozy Adventure</h1>
          <p class="subtitle">Explore • Build • Survive</p>
        </div>

        <div class="character-select" role="radiogroup" aria-label="Choose your character">
          <span class="char-label">Character</span>
          <div class="char-options">
            <button type="button" class="char-option active" id="char-male" data-model="male" role="radio" aria-checked="true">
              <span class="char-icon">🧑</span>
              <span class="char-name">Male</span>
            </button>
            <button type="button" class="char-option" id="char-female" data-model="female" role="radio" aria-checked="false">
              <span class="char-icon">👩</span>
              <span class="char-name">Female</span>
            </button>
          </div>
          <label class="char-label" for="player-name" style="margin-top:14px;">Name</label>
          <input class="mp-input" id="player-name" type="text" autocomplete="off" maxlength="${MAX_DISPLAY_NAME_LENGTH}"
            placeholder="Your name (shown to other players)"
            style="width:100%;box-sizing:border-box;margin-top:6px;padding:12px 14px;border:3px solid #CD853F;border-radius:14px;background:#FFF8E7;color:#5b3a1a;font-family:'Nunito',sans-serif;font-size:1rem;" />
        </div>

        <div class="menu-buttons">
          <button class="menu-btn primary" id="start-game">
            <span class="btn-icon">🏠</span>
            <span class="btn-text">Start Adventure</span>
          </button>
          
          <button class="menu-btn" id="continue-game" style="display: none;">
            <span class="btn-icon">📖</span>
            <span class="btn-text">Continue</span>
          </button>
          
          <button class="menu-btn" id="load-game">
            <span class="btn-icon">💾</span>
            <span class="btn-text">Load Game</span>
          </button>
          
          <button class="menu-btn" id="save-game" style="display: none;">
            <span class="btn-icon">💿</span>
            <span class="btn-text">Save Game</span>
          </button>

          <button class="menu-btn" id="mp-toggle" type="button" aria-expanded="false">
            <span class="btn-icon">🌐</span>
            <span class="btn-text">Multiplayer</span>
          </button>

          <div class="mp-fields" id="mp-fields" style="display: none;">
            <input class="mp-input" id="mp-url" type="text" inputmode="url" autocomplete="off"
              placeholder="Server URL (wss://your-server)"
              style="width:100%;box-sizing:border-box;margin-bottom:10px;padding:12px 14px;border:3px solid #CD853F;border-radius:14px;background:#FFF8E7;color:#5b3a1a;font-family:'Nunito',sans-serif;font-size:1rem;" />
            <input class="mp-input" id="mp-password" type="password" autocomplete="off"
              placeholder="Password (if the server has one)"
              style="width:100%;box-sizing:border-box;margin-bottom:10px;padding:12px 14px;border:3px solid #CD853F;border-radius:14px;background:#FFF8E7;color:#5b3a1a;font-family:'Nunito',sans-serif;font-size:1rem;" />
            <button class="menu-btn primary" id="mp-join" type="button">
              <span class="btn-icon">🔌</span>
              <span class="btn-text">Join Server</span>
            </button>
          </div>
        </div>

        <!-- Save/Load Modal -->
        <div id="save-load-modal" class="save-load-modal" style="display: none;">
          <div class="modal-content">
            <div class="modal-header">
              <h2 id="modal-title">Load Game</h2>
              <button class="close-btn" id="close-modal">&times;</button>
            </div>
            <div class="modal-body">
              <div class="save-slots" id="save-slots">
                <!-- Save slots will be populated dynamically -->
              </div>
            </div>
            <div class="modal-footer">
              <button class="menu-btn" id="modal-cancel">Cancel</button>
            </div>
          </div>
        </div>
      </div>
    `;
    
    this.createStyles();
    this.setupEventListeners();
    
    document.body.appendChild(this.menuElement);
  }

  createStyles() {
    const style = document.createElement('style');
    style.textContent = `
      @import url('/assets/external/fonts.googleapis.com/css2__qs_family_Fredoka_One_wght_400_family_Nunito_wght_400_600_700_display_swap.css');
      
      .main-menu {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        height: 100dvh;
        z-index: 10000;
        font-family: 'Nunito', sans-serif;
        display: flex;
        align-items: flex-start;
        justify-content: center;
        overflow-x: hidden;
        overflow-y: auto;
        overscroll-behavior: contain;
        -webkit-overflow-scrolling: touch;
        box-sizing: border-box;
        padding: max(16px, env(safe-area-inset-top)) max(12px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(12px, env(safe-area-inset-left));
      }
      
      .menu-background {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(
          135deg,
          rgba(139, 69, 19, 0.9) 0%,
          rgba(160, 82, 45, 0.85) 25%,
          rgba(210, 180, 140, 0.8) 50%,
          rgba(245, 222, 179, 0.85) 75%,
          rgba(139, 69, 19, 0.9) 100%
        );
        backdrop-filter: blur(10px);
        animation: backgroundShift 20s ease-in-out infinite;
      }
      
      @keyframes backgroundShift {
        0%, 100% { opacity: 0.9; }
        50% { opacity: 0.7; }
      }
      
      .menu-content {
        position: relative;
        z-index: 2;
        text-align: center;
        max-width: 500px;
        width: 90%;
        margin: auto 0;
        padding: 40px;
        background: linear-gradient(145deg, rgba(245, 222, 179, 0.95), rgba(222, 184, 135, 0.9));
        border: 4px solid #8B4513;
        border-radius: 30px;
        box-shadow: 
          0 20px 60px rgba(139, 69, 19, 0.6),
          inset 0 4px 8px rgba(255, 255, 255, 0.3),
          inset 0 -4px 8px rgba(0, 0, 0, 0.1);
        animation: menuFloat 6s ease-in-out infinite;
      }

      .mp-fields {
        width: 100%;
      }
      
      @keyframes menuFloat {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-10px); }
      }
      
      .game-title h1 {
        font-family: 'Fredoka One', cursive;
        font-size: 3.5rem;
        color: #8B4513;
        margin: 0 0 10px 0;
        text-shadow: 
          3px 3px 0px rgba(160, 82, 45, 0.8),
          6px 6px 20px rgba(139, 69, 19, 0.4);
        background: linear-gradient(45deg, #8B4513, #CD853F, #D2691E);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        animation: titleGlow 3s ease-in-out infinite alternate;
      }
      
      @keyframes titleGlow {
        from { filter: drop-shadow(0 0 5px rgba(139, 69, 19, 0.3)); }
        to { filter: drop-shadow(0 0 15px rgba(139, 69, 19, 0.6)); }
      }
      
      .subtitle {
        font-size: 1.2rem;
        color: #A0522D;
        margin: 0 0 30px 0;
        font-weight: 600;
        letter-spacing: 2px;
        text-transform: uppercase;
      }
      
      .character-select {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
        margin-bottom: 24px;
      }

      .char-label {
        font-family: 'Nunito', sans-serif;
        font-size: 0.95rem;
        font-weight: 700;
        letter-spacing: 2px;
        text-transform: uppercase;
        color: #8B4513;
        opacity: 0.85;
      }

      .char-options {
        display: flex;
        gap: 12px;
      }

      .char-option {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
        width: 96px;
        padding: 12px 10px;
        background: linear-gradient(145deg, #F5DEB3, #DEB887);
        border: 3px solid #CD853F;
        border-radius: 18px;
        color: #8B4513;
        font-family: 'Nunito', sans-serif;
        font-size: 0.95rem;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: inset 0 2px 4px rgba(255, 255, 255, 0.5);
      }

      .char-option .char-icon {
        font-size: 1.8rem;
        line-height: 1;
      }

      .char-option:hover {
        transform: translateY(-2px);
        border-color: #DAA520;
      }

      .char-option.active {
        background: linear-gradient(145deg, #FFFACD, #F0E68C);
        border-color: #DAA520;
        box-shadow:
          0 6px 18px rgba(218, 165, 32, 0.45),
          inset 0 2px 6px rgba(255, 255, 255, 0.7);
      }

      .char-option:focus-visible {
        outline: 3px solid #DAA520;
        outline-offset: 2px;
      }

      .menu-buttons {
        display: flex;
        flex-direction: column;
        gap: 15px;
        margin-bottom: 0;
      }
      
      .menu-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        padding: 16px 24px;
        background: linear-gradient(145deg, #F5DEB3, #DEB887);
        border: 3px solid #CD853F;
        border-radius: 20px;
        color: #8B4513;
        font-family: 'Nunito', sans-serif;
        font-size: 1.1rem;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 
          0 6px 20px rgba(139, 69, 19, 0.3),
          inset 0 2px 4px rgba(255, 255, 255, 0.6),
          inset 0 -2px 4px rgba(0, 0, 0, 0.1);
        position: relative;
        overflow: hidden;
      }
      
      .menu-btn::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
        transition: left 0.5s ease;
      }
      
      .menu-btn:hover::before {
        left: 100%;
      }
      
      .menu-btn:hover {
        transform: translateY(-3px) scale(1.02);
        background: linear-gradient(145deg, #FFFACD, #F0E68C);
        border-color: #DAA520;
        box-shadow: 
          0 8px 25px rgba(139, 69, 19, 0.4),
          inset 0 2px 6px rgba(255, 255, 255, 0.7),
          inset 0 -3px 6px rgba(0, 0, 0, 0.1);
      }
      
      .menu-btn:active {
        transform: translateY(-1px) scale(0.98);
      }
      
      .menu-btn.primary {
        background: linear-gradient(145deg, #98FB98, #90EE90);
        border-color: #32CD32;
        color: #2E8B57;
        box-shadow: 
          0 8px 25px rgba(50, 205, 50, 0.4),
          inset 0 2px 6px rgba(255, 255, 255, 0.8),
          inset 0 -3px 6px rgba(0, 0, 0, 0.1);
      }
      
      .menu-btn.primary:hover {
        background: linear-gradient(145deg, #ADFF2F, #98FB98);
        border-color: #228B22;
        box-shadow: 
          0 10px 30px rgba(50, 205, 50, 0.6),
          inset 0 2px 8px rgba(255, 255, 255, 0.9),
          inset 0 -4px 8px rgba(0, 0, 0, 0.1);
      }
      
      .btn-icon {
        font-size: 1.4rem;
        filter: drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.2));
      }
      
      .btn-text {
        font-weight: 700;
        letter-spacing: 0.5px;
      }
      
      /* Mobile responsive */
      @media (max-width: 768px) {
        .menu-content {
          width: 95%;
          padding: 30px 20px;
          max-width: none;
        }
        
        .game-title h1 {
          font-size: 2.5rem;
        }
        
        .subtitle {
          font-size: 1rem;
          margin-bottom: 25px;
        }
        
        .menu-btn {
          padding: 14px 20px;
          font-size: 1rem;
          gap: 10px;
        }
        
        .btn-icon {
          font-size: 1.2rem;
        }
      }
      
      @media (max-width: 480px) {
        .menu-content {
          padding: 25px 15px;
        }
        
        .game-title h1 {
          font-size: 2rem;
        }
        
        .menu-buttons {
          gap: 12px;
        }
        
        .menu-btn {
          padding: 12px 16px;
          font-size: 0.95rem;
        }
      }

      @media (max-height: 720px), (max-width: 768px) and (orientation: landscape) {
        .main-menu {
          padding-top: max(8px, env(safe-area-inset-top));
          padding-bottom: max(8px, env(safe-area-inset-bottom));
        }

        .menu-content {
          width: min(92vw, 560px);
          max-height: calc(100dvh - max(16px, env(safe-area-inset-top)) - max(16px, env(safe-area-inset-bottom)));
          overflow-y: auto;
          padding: 14px 22px;
          border-radius: 18px;
          animation: none;
        }

        .game-title h1 {
          font-size: 1.8rem;
          margin-bottom: 2px;
        }

        .subtitle {
          font-size: 0.78rem;
          margin-bottom: 10px;
          letter-spacing: 1px;
        }

        .character-select {
          gap: 6px;
          margin-bottom: 12px;
        }

        .char-label {
          font-size: 0.72rem;
          letter-spacing: 1.4px;
        }

        .char-options {
          gap: 8px;
        }

        .char-option {
          width: 88px;
          padding: 8px 8px;
          border-radius: 14px;
        }

        .char-option .char-icon {
          font-size: 1.35rem;
        }

        .menu-buttons {
          gap: 9px;
        }

        .menu-btn {
          min-height: 48px;
          padding: 9px 16px;
          border-radius: 14px;
          font-size: 0.95rem;
        }

        .mp-input {
          min-height: 44px !important;
          margin-bottom: 8px !important;
          padding: 9px 12px !important;
          border-radius: 12px !important;
          font-size: 0.95rem !important;
        }
      }
      
      /* Save/Load Modal Styles */
      .save-load-modal {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.8);
        z-index: 10001;
        display: flex;
        align-items: center;
        justify-content: center;
        animation: fadeIn 0.3s ease;
      }
      
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      
      .modal-content {
        background: linear-gradient(145deg, rgba(245, 222, 179, 0.98), rgba(222, 184, 135, 0.95));
        border: 4px solid #8B4513;
        border-radius: 20px;
        width: 90%;
        max-width: 600px;
        max-height: 80vh;
        overflow-y: auto;
        box-shadow: 0 20px 60px rgba(139, 69, 19, 0.8);
      }
      
      .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px 30px;
        border-bottom: 2px solid rgba(139, 69, 19, 0.3);
      }
      
      .modal-header h2 {
        font-family: 'Fredoka One', cursive;
        color: #8B4513;
        margin: 0;
        font-size: 1.8rem;
      }
      
      .close-btn {
        background: none;
        border: none;
        font-size: 2rem;
        color: #8B4513;
        cursor: pointer;
        padding: 0;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        transition: background-color 0.2s;
      }
      
      .close-btn:hover {
        background-color: rgba(139, 69, 19, 0.1);
      }
      
      .modal-body {
        padding: 30px;
      }
      
      .save-slots {
        display: flex;
        flex-direction: column;
        gap: 15px;
      }
      
      .save-slot {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 20px;
        background: linear-gradient(145deg, #F5DEB3, #DEB887);
        border: 2px solid #CD853F;
        border-radius: 15px;
        transition: all 0.2s ease;
        cursor: pointer;
      }
      
      .save-slot:hover {
        background: linear-gradient(145deg, #FFFACD, #F0E68C);
        border-color: #DAA520;
        transform: translateY(-2px);
        box-shadow: 0 4px 15px rgba(139, 69, 19, 0.3);
      }
      
      .save-slot.empty {
        opacity: 0.6;
        font-style: italic;
      }
      
      .slot-info {
        flex: 1;
      }
      
      .slot-header {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 5px;
      }
      
      .slot-number {
        font-weight: bold;
        color: #8B4513;
        font-size: 1.1rem;
      }
      
      .slot-timestamp {
        color: #A0522D;
        font-size: 0.9rem;
      }
      
      .slot-details {
        font-size: 0.85rem;
        color: #8B4513;
      }
      
      .slot-actions {
        display: flex;
        gap: 10px;
      }
      
      .slot-btn {
        padding: 8px 15px;
        border: 2px solid #CD853F;
        border-radius: 10px;
        background: linear-gradient(145deg, #F5DEB3, #DEB887);
        color: #8B4513;
        font-family: 'Nunito', sans-serif;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        font-size: 0.9rem;
      }
      
      .slot-btn:hover {
        background: linear-gradient(145deg, #FFFACD, #F0E68C);
        transform: translateY(-1px);
      }
      
      .slot-btn.delete {
        background: linear-gradient(145deg, #FFB6C1, #FF69B4);
        border-color: #FF1493;
        color: #8B0000;
      }
      
      .slot-btn.delete:hover {
        background: linear-gradient(145deg, #FFC0CB, #FFB6C1);
      }
      
      .modal-footer {
        padding: 20px 30px;
        border-top: 2px solid rgba(139, 69, 19, 0.3);
        display: flex;
        justify-content: center;
      }
      
      @media (max-width: 768px) {
        .modal-content {
          width: 95%;
          margin: 20px;
        }
        
        .modal-header {
          padding: 15px 20px;
        }
        
        .modal-body {
          padding: 20px;
        }
        
        .save-slot {
          flex-direction: column;
          align-items: flex-start;
          gap: 15px;
        }
        
        .slot-actions {
          align-self: stretch;
          justify-content: space-between;
        }
        
        .slot-btn {
          flex: 1;
          text-align: center;
        }
      }
    `;
    
    document.head.appendChild(style);
  }

  setupBackground() {
    // Create a simple 3D background scene
    this.backgroundScene = new THREE.Scene();
    this.backgroundCamera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    this.backgroundRenderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    
    this.backgroundRenderer.setSize(window.innerWidth, window.innerHeight);
    this.backgroundRenderer.setClearColor(0x87CEEB, 0.3);
    
    // Insert background canvas behind menu
    const menuBackground = this.qs('.menu-background');
    menuBackground.appendChild(this.backgroundRenderer.domElement);
    this.backgroundRenderer.domElement.style.position = 'absolute';
    this.backgroundRenderer.domElement.style.top = '0';
    this.backgroundRenderer.domElement.style.left = '0';
    this.backgroundRenderer.domElement.style.zIndex = '-1';
    
    // Add ambient light
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    this.backgroundScene!.add(ambientLight);
    
    // Add directional light
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(5, 5, 5);
    this.backgroundScene!.add(directionalLight);
    
    // Create floating cubes for background animation
    this.createFloatingElements();
    
    this.backgroundCamera.position.z = 10;
  }

  createFloatingElements() {
    const geometries = [
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.SphereGeometry(0.5, 16, 12),
      new THREE.ConeGeometry(0.5, 1, 8)
    ];
    
    const materials = [
      new THREE.MeshLambertMaterial({ color: 0x8B4513, transparent: true, opacity: 0.3 }),
      new THREE.MeshLambertMaterial({ color: 0xDEB887, transparent: true, opacity: 0.4 }),
      new THREE.MeshLambertMaterial({ color: 0xF5DEB3, transparent: true, opacity: 0.3 })
    ];
    
    this.floatingElements = [];
    
    for (let i = 0; i < 20; i++) {
      const geometry = geometries[Math.floor(Math.random() * geometries.length)];
      const material = materials[Math.floor(Math.random() * materials.length)];
      const mesh = new THREE.Mesh(geometry, material);
      
      // Random position
      mesh.position.set(
        (Math.random() - 0.5) * 30,
        (Math.random() - 0.5) * 20,
        (Math.random() - 0.5) * 20
      );
      
      // Random rotation
      mesh.rotation.set(
        Math.random() * Math.PI,
        Math.random() * Math.PI,
        Math.random() * Math.PI
      );
      
      // Random scale
      const scale = 0.5 + Math.random() * 1.5;
      mesh.scale.setScalar(scale);
      
      // Store animation properties
      mesh.userData = {
        rotationSpeed: {
          x: (Math.random() - 0.5) * 0.02,
          y: (Math.random() - 0.5) * 0.02,
          z: (Math.random() - 0.5) * 0.02
        },
        floatSpeed: 0.001 + Math.random() * 0.002,
        floatOffset: Math.random() * Math.PI * 2
      };
      
      this.backgroundScene!.add(mesh);
      this.floatingElements.push(mesh);
    }
  }

  setupEventListeners() {
    // Start Game button
    const startBtn = this.qs('#start-game');
    startBtn.addEventListener('click', () => {
      this.startGame();
    });

    // Multiplayer panel: toggle the fields, then connect on "Join Server".
    const mpToggle = this.qs('#mp-toggle');
    const mpFields = this.qs('#mp-fields');
    mpToggle.addEventListener('click', () => {
      const open = mpFields.style.display !== 'none';
      mpFields.style.display = open ? 'none' : 'block';
      mpToggle.setAttribute('aria-expanded', String(!open));
    });
    // Pre-fill the last server used (best-effort; localStorage may be unavailable, e.g. private mode).
    try {
      const last = localStorage.getItem('cozy:lastServer');
      (this.qs('#mp-url') as HTMLInputElement).value = last || 'wss://cozy.gomezleon.es';
    } catch {
      (this.qs('#mp-url') as HTMLInputElement).value = 'wss://cozy.gomezleon.es';
    }
    this.qs('#mp-join').addEventListener('click', () => {
      void this.joinMultiplayer();
    });

    // Player name: persisted in localStorage and sent on multiplayer join, where it
    // labels this player's avatar for peers. Sanitized on the way in and out.
    const nameInput = this.qs('#player-name') as HTMLInputElement;
    nameInput.maxLength = MAX_DISPLAY_NAME_LENGTH;
    nameInput.value = getPlayerName();
    nameInput.addEventListener('input', () => setPlayerName(nameInput.value));
    nameInput.addEventListener('blur', () => {
      nameInput.value = setPlayerName(nameInput.value); // reflect the canonical clamp
    });

    // Character selector (only affects a NEW game; loaded saves keep their model)
    const charOptions = this.menuElement.querySelectorAll<HTMLElement>('.char-option');
    charOptions.forEach((opt) => {
      opt.addEventListener('click', () => {
        this.selectedModel = opt.dataset.model === 'female' ? 'female' : 'male';
        charOptions.forEach((o) => {
          const isActive = o === opt;
          o.classList.toggle('active', isActive);
          o.setAttribute('aria-checked', String(isActive));
        });
      });
    });

    // Continue Game button
    const continueBtn = this.qs('#continue-game');
    continueBtn.addEventListener('click', () => {
      this.continueGame();
    });
    
    // Load Game button
    const loadBtn = this.qs('#load-game');
    loadBtn.addEventListener('click', () => {
      this.showLoadModal();
    });
    
    // Save Game button
    const saveBtn = this.qs('#save-game');
    saveBtn.addEventListener('click', () => {
      this.showSaveModal();
    });
    
    // Modal event listeners
    const modal = this.qs('#save-load-modal');
    const closeBtn = this.qs('#close-modal');
    const cancelBtn = this.qs('#modal-cancel');
    
    closeBtn.addEventListener('click', () => {
      this.hideModal();
    });
    
    cancelBtn.addEventListener('click', () => {
      this.hideModal();
    });
    
    // Close modal when clicking outside
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        this.hideModal();
      }
    });
    
    // Handle window resize
    window.addEventListener('resize', () => {
      if (this.backgroundRenderer && this.backgroundCamera) {
        this.backgroundCamera.aspect = window.innerWidth / window.innerHeight;
        this.backgroundCamera.updateProjectionMatrix();
        this.backgroundRenderer.setSize(window.innerWidth, window.innerHeight);
      }
    });
  }

  startBackgroundAnimation() {
    const animate = () => {
      if (!this.isVisible) return;
      
      this.animationId = requestAnimationFrame(animate);
      
      // Animate floating elements
      this.floatingElements.forEach((element, index) => {
        element.rotation.x += element.userData.rotationSpeed.x;
        element.rotation.y += element.userData.rotationSpeed.y;
        element.rotation.z += element.userData.rotationSpeed.z;
        
        // Floating motion
        element.position.y += Math.sin(Date.now() * element.userData.floatSpeed + element.userData.floatOffset) * 0.01;
      });
      
      this.backgroundRenderer!.render(this.backgroundScene!, this.backgroundCamera!);
    };
    
    animate();
  }

  startGame() {
    console.log('Starting game...');
    this.hide();

    // Lock in the chosen avatar before the world (and Player) is built.
    setSelectedPlayerModel(this.selectedModel);

    // Initialize the game if it hasn't been started yet
    if (this.gameInstance && typeof this.gameInstance.startGame === 'function') {
      this.gameInstance.startGame();
    }
  }

  /** Connect to a multiplayer server, then start the game in network mode. */
  async joinMultiplayer(): Promise<void> {
    const url = (this.qs('#mp-url') as HTMLInputElement).value.trim();
    const password = (this.qs('#mp-password') as HTMLInputElement).value;
    if (!url) {
      this.showMessage('Enter a server URL to join.', 'error');
      return;
    }
    try {
      localStorage.setItem('cozy:lastServer', url);
    } catch {
      /* best-effort */
    }

    // Lock in the chosen avatar before the world (and Player) is built.
    setSelectedPlayerModel(this.selectedModel);

    const joinBtn = this.qs('#mp-join') as HTMLButtonElement;
    const label = joinBtn.querySelector<HTMLElement>('.btn-text');
    const originalLabel = label?.textContent ?? 'Join Server';
    joinBtn.disabled = true;
    if (label) label.textContent = 'Connecting…';

    try {
      // startGame joins first (before building anything); on success it hides the
      // menu and builds the shared world from the server's seed.
      await this.gameInstance.startGame(undefined, undefined, { url, password: password || undefined });
    } catch (err) {
      // Bad URL, server down, wrong password, world full, or version mismatch: the
      // game built nothing, so the menu is still up. Surface a clear error.
      const msg = err instanceof Error ? err.message : 'connection failed';
      this.showMessage(`Could not join: ${msg}`, 'error');
    } finally {
      joinBtn.disabled = false;
      if (label) label.textContent = originalLabel;
    }
  }
  // Check for existing saves and update UI
  updateMenuState() {
    const saveSlots = this.gameInstance.getSaveSlots();
    const hasAnySave = saveSlots.some((slot: any) => slot.exists);
    
    // Show continue button if there are any saves
    const continueBtn = this.qs('#continue-game');
    if (hasAnySave) {
      continueBtn.style.display = 'flex';
    } else {
      continueBtn.style.display = 'none';
    }
    
    // Show save button only if game is started
    const saveBtn = this.qs('#save-game');
    if (this.gameInstance.isGameStarted) {
      saveBtn.style.display = 'flex';
    } else {
      saveBtn.style.display = 'none';
    }
  }
  continueGame() {
    // Load the most recent save
    const saveSlots = this.gameInstance.getSaveSlots();
    let mostRecentSlot = null;
    let mostRecentTime = 0;
    
    for (const slot of saveSlots) {
      if (slot.exists && slot.metadata && slot.metadata.timestamp > mostRecentTime) {
        mostRecentSlot = slot.slotNumber;
        mostRecentTime = slot.metadata.timestamp;
      }
    }
    
    if (mostRecentSlot !== null) {
      this.loadGame(mostRecentSlot);
    }
  }
  showLoadModal() {
    this.currentModalType = 'load';
    const modal = this.qs('#save-load-modal');
    const title = this.qs('#modal-title');
    
    title.textContent = 'Load Game';
    this.populateSaveSlots();
    modal.style.display = 'flex';
  }
  showSaveModal() {
    this.currentModalType = 'save';
    const modal = this.qs('#save-load-modal');
    const title = this.qs('#modal-title');
    
    title.textContent = 'Save Game';
    this.populateSaveSlots();
    modal.style.display = 'flex';
  }
  hideModal() {
    const modal = this.qs('#save-load-modal');
    modal.style.display = 'none';
  }
  populateSaveSlots() {
    const slotsContainer = this.qs('#save-slots');
    const saveSlots = this.gameInstance.getSaveSlots();
    
    slotsContainer.innerHTML = '';
    
    saveSlots.forEach((slot: any) => {
      const slotElement = document.createElement('div');
      slotElement.className = `save-slot ${slot.exists ? '' : 'empty'}`;
      
      if (slot.exists && slot.metadata) {
        const date = new Date(slot.metadata.timestamp);
        const playTime = this.formatPlayTime(slot.metadata.playTime || 0);
        
        slotElement.innerHTML = `
          <div class="slot-info">
            <div class="slot-header">
              <span class="slot-number">Slot ${slot.slotNumber + 1}</span>
              <span class="slot-timestamp">${date.toLocaleDateString()} ${date.toLocaleTimeString()}</span>
            </div>
            <div class="slot-details">
              Play Time: ${playTime} • Location: ${slot.metadata.location || 'Unknown'}
            </div>
          </div>
          <div class="slot-actions">
            <button class="slot-btn load-btn" data-slot="${slot.slotNumber}">
              ${this.currentModalType === 'save' ? 'Overwrite' : 'Load'}
            </button>
            <button class="slot-btn delete" data-slot="${slot.slotNumber}">Delete</button>
          </div>
        `;
      } else {
        slotElement.innerHTML = `
          <div class="slot-info">
            <div class="slot-header">
              <span class="slot-number">Slot ${slot.slotNumber + 1}</span>
            </div>
            <div class="slot-details">Empty Slot</div>
          </div>
          <div class="slot-actions">
            <button class="slot-btn load-btn" data-slot="${slot.slotNumber}" ${this.currentModalType === 'load' ? 'disabled' : ''}>
              ${this.currentModalType === 'save' ? 'Save Here' : 'Empty'}
            </button>
          </div>
        `;
      }
      
      slotsContainer.appendChild(slotElement);
    });
    
    // Add event listeners to slot buttons
    this.setupSlotEventListeners();
  }
  setupSlotEventListeners() {
    const loadBtns = this.menuElement.querySelectorAll<HTMLElement>('.load-btn');
    const deleteBtns = this.menuElement.querySelectorAll<HTMLElement>('.delete');
    
    loadBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const slotNumber = parseInt(btn.dataset.slot!);
        
        if (this.currentModalType === 'save') {
          this.saveGame(slotNumber);
        } else {
          this.loadGame(slotNumber);
        }
      });
    });
    
    deleteBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const slotNumber = parseInt(btn.dataset.slot!);
        this.deleteSave(slotNumber);
      });
    });
  }
  async saveGame(slotNumber: number): Promise<void> {
    if (!this.gameInstance.isGameStarted) {
      alert('You must start a game before you can save!');
      return;
    }
    
    try {
      const success = this.gameInstance.saveGame(slotNumber);
      if (success) {
        console.log(`Game saved to slot ${slotNumber + 1}`);
        this.hideModal();
        this.updateMenuState();
        
        // Show success message
        this.showMessage(`Game saved to Slot ${slotNumber + 1}!`, 'success');
      } else {
        throw new Error('Save operation failed');
      }
    } catch (error) {
      console.error('Failed to save game:', error);
      this.showMessage('Failed to save game. Please try again.', 'error');
    }
  }
  async loadGame(slotNumber: number): Promise<void> {
    try {
      const success = await this.gameInstance.loadGame(slotNumber);
      if (success) {
        console.log(`Game loaded from slot ${slotNumber + 1}`);
        this.hideModal();
        this.updateMenuState();
        
        // Show success message
        this.showMessage(`Game loaded from Slot ${slotNumber + 1}!`, 'success');
      } else {
        throw new Error('Load operation failed');
      }
    } catch (error) {
      console.error('Failed to load game:', error);
      this.showMessage('Failed to load game. The save file may be corrupted.', 'error');
    }
  }
  deleteSave(slotNumber: number): void {
    if (confirm(`Are you sure you want to delete Slot ${slotNumber + 1}? This action cannot be undone.`)) {
      try {
        const success = this.gameInstance.deleteSave(slotNumber);
        if (success) {
          console.log(`Save slot ${slotNumber + 1} deleted`);
          this.populateSaveSlots(); // Refresh the slots display
          this.updateMenuState();
          
          this.showMessage(`Slot ${slotNumber + 1} deleted.`, 'info');
        } else {
          throw new Error('Delete operation failed');
        }
      } catch (error) {
        console.error('Failed to delete save:', error);
        this.showMessage('Failed to delete save file.', 'error');
      }
    }
  }
  formatPlayTime(milliseconds: number): string {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    } else {
      return `${seconds}s`;
    }
  }
  showMessage(text: string, type = 'info'): void {
    // Create temporary message element
    const message = document.createElement('div');
    message.className = `menu-message ${type}`;
    message.textContent = text;
    message.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 15px 20px;
      background: ${type === 'success' ? '#4CAF50' : type === 'error' ? '#f44336' : '#2196F3'};
      color: white;
      border-radius: 10px;
      font-family: 'Nunito', sans-serif;
      font-weight: 600;
      z-index: 10002;
      animation: slideIn 0.3s ease;
    `;
    
    document.body.appendChild(message);
    
    // Remove message after 3 seconds
    setTimeout(() => {
      message.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => {
        message.remove();
      }, 300);
    }, 3000);
  }

  show() {
    if (this.menuElement) {
      this.menuElement.style.display = 'flex';
      this.isVisible = true;
      this.startBackgroundAnimation();
      this.updateMenuState(); // Update button visibility when showing menu
    }
  }

  hide() {
    if (this.menuElement) {
      this.menuElement.style.display = 'none';
      this.isVisible = false;
      
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
        this.animationId = null;
      }
    }
  }

  destroy() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    
    if (this.backgroundRenderer) {
      this.backgroundRenderer.dispose();
    }
    
    if (this.menuElement) {
      this.menuElement.remove();
    }
  }
}
